import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ResponsiveImage from './ResponsiveImage';
import { getImageUrl } from '../lib/supabase';

const servicesData = [
  { title: 'Fizioterapija', image: 'assets/phisio.webp', route: '/fizioterapija' },
  { title: 'Fizikalni pregled', image: 'assets/knee-pain.webp', route: '/fizikalni-pregled' },
  { title: 'Ortopedija', image: 'assets/ortho.webp', route: '/ortopedija' },
  { title: 'Kineziterapija', image: 'assets/kinesi.webp', route: '/kineziterapija' },
  { title: 'Ultrazvučna dijagnostika', image: 'assets/ultrasound.webp', route: '/ultrazvuk' },
  { title: 'Emmett terapija', image: 'assets/back-pain.webp', route: '/emmett-terapija' },
  { title: 'Bowen terapija', image: 'assets/arm-pain.webp', route: '/bowen-terapija' },
];

const otherServicesData = [
  { title: 'Hijaluron', route: '/hijaluron' },
  { title: 'Blokada', route: '/blokada' },
  { title: 'Fokusirani udarni val', route: '/udarni-val' },
  { title: 'Dry needling', route: '/dry-needling' },
  { title: 'Limfna drenaža', route: '/limfna-drenaža' },
];

function ServiceCard({ service, isDesktop }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link to={service.route} className="text-decoration-none">
      <div
        style={{
          position: 'relative',
          height: '320px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          cursor: 'pointer',
          borderRadius: '0.5rem',
          boxShadow: hovered
            ? '0 12px 30px rgba(66, 89, 168, 0.2)'
            : '0 4px 6px rgba(0, 0, 0, 0.05)',
          transition: 'box-shadow 0.6s ease',
        }}
        onMouseEnter={() => isDesktop && setHovered(true)}
        onMouseLeave={() => isDesktop && setHovered(false)}
      >
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            transform: hovered ? 'scale(1.07)' : 'scale(1)',
            transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            zIndex: 0,
          }}
        >
          <ResponsiveImage
            src={getImageUrl(service.image.replace('assets/', ''))}
            sizes="(max-width: 768px) 100vw, (max-width: 992px) 50vw, 25vw"
            alt={service.title}
            objectFit="cover"
            objectPosition="top"
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(136.8deg, rgba(66, 89, 168, 0) 0%, rgba(16, 61, 96, 0.62) 100%)',
            transition: 'background 0.6s ease',
            zIndex: 1,
          }}
        />

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            padding: '1.5rem',
            transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          <h4
            className="f-h4 mb-0"
            style={{
              color: '#fff',
              fontWeight: 600,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              fontSize: '1.1rem',
            }}
          >
            {service.title}
          </h4>
          <div
            style={{
              marginTop: '1rem',
              width: hovered ? '60px' : '40px',
              height: '3px',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '2px',
              transition: 'width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}
          />
        </div>
      </div>
    </Link>
  );
}

function Services() {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const Container = isDesktop ? motion.div : 'div';
  const motionProps = isDesktop ? {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.01 },
    transition: { duration: 1, ease: 'easeOut' }
  } : {};

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section
      className="page-section py-5"
      id="portfolio"
      style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
        overflow: 'hidden'
      }}
    >
      <div className="position-absolute rounded-circle" style={{ width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(66, 89, 168, 0.08), transparent)', top: '-100px', right: '-50px', filter: 'blur(40px)', zIndex: 0 }} />
      <div className="position-absolute rounded-circle" style={{ width: '250px', height: '250px', background: 'radial-gradient(circle, rgba(46, 141, 215, 0.06), transparent)', bottom: '-80px', left: '-30px', filter: 'blur(35px)', zIndex: 0 }} />

      <Container className="container position-relative" style={{ zIndex: 1 }} {...motionProps}>
        <div className="text-center mb-5">
          <h2 className="f-h1 text-uppercase responsive-heading mb-3" style={{ color: '#4259A8', letterSpacing: '2px' }}>
            Naše Usluge
          </h2>
          <div className="mx-auto mb-4" style={{ width: '100px', height: '4px', background: 'linear-gradient(90deg, #4259A8, #2E8DD7)', borderRadius: '2px' }} />
          <p className="f-p1" style={{ color: '#6c757d', maxWidth: '600px', margin: '0 auto' }}>
            Pružamo širok spektar stručnih usluga prilagođenih vašim potrebama
          </p>
        </div>

        <motion.div
          className="row g-3 mb-5"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.01 }}
        >
          {servicesData.map((service, index) => (
            <motion.div className="col-lg-3 col-md-6" key={index} variants={itemVariants}>
              <ServiceCard service={service} isDesktop={isDesktop} />
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-5 pt-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <div className="text-center mb-5">
            <h3 className="f-h2 mb-3" style={{ color: '#4259A8' }}>Dodatne Usluge</h3>
            <div className="mx-auto mb-4" style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #4259A8, #2E8DD7)', borderRadius: '2px' }} />
          </div>

          <div className="row align-items-center g-4">
            <div className="col-lg-5" style={{ marginTop: '0' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                style={{ position: 'relative', borderRadius: '0', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', height: '400px' }}
              >
                <ResponsiveImage
                  src={getImageUrl('dodatne-usluge.webp')}
                  sizes="(max-width: 768px) 100vw, (max-width: 992px) 100vw, 42vw"
                  alt="Dodatne usluge"
                  objectFit="cover"
                  style={{ width: '100%', height: '100%' }}
                />
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(135deg, rgba(66, 89, 168, 0.15), rgba(46, 141, 215, 0.15))' }} />
              </motion.div>
            </div>

            <div className="col-lg-7">
              <motion.ul
                className="list-unstyled"
                style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {otherServicesData.map((service, index) => (
                  <motion.li key={index} variants={itemVariants}>
                    <Link
                      to={service.route}
                      className="text-decoration-none"
                      style={{
                        display: 'flex', alignItems: 'center',
                        padding: '1.25rem 1.75rem',
                        background: 'radial-gradient(ellipse at top left, rgba(66, 89, 168, 0.18), rgba(46, 141, 215, 0.12), rgba(66, 89, 168, 0.08))',
                        borderRadius: '12px', border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#4259A8';
                        e.currentTarget.style.background = 'radial-gradient(ellipse at top left, rgba(66, 89, 168, 0.28), rgba(46, 141, 215, 0.22), rgba(66, 89, 168, 0.15))';
                        e.currentTarget.style.transform = 'translateX(8px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(66, 89, 168, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e9ecef';
                        e.currentTarget.style.background = 'radial-gradient(ellipse at top left, rgba(66, 89, 168, 0.18), rgba(46, 141, 215, 0.12), rgba(66, 89, 168, 0.08))';
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                      }}
                    >
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4259A8', marginRight: '1rem', flexShrink: 0 }} />
                      <span style={{ color: '#4259A8', fontWeight: 500, fontSize: '1.05rem' }}>{service.title}</span>
                      <svg style={{ marginLeft: 'auto', width: '20px', height: '20px', color: '#4259A8', opacity: 0.6 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

export default Services;